import React, { useState, useRef, useEffect } from "react";
import {
  MapPin,
  Calendar,
  Users,
  Search,
  Zap,
  Tag,
  Gift,
  Headphones,
  Plus,
  Minus,
  Star,
  Smartphone,
} from "lucide-react";
import "./CSS/Hotel.css";
import heroBg from "./Images/hotelbg.png";
import sliderimage1 from "./Images/niraj-golhar-7sASTzCacdA-unsplash.jpg";
import sliderimage2 from "./Images/sara-dubler-Koei_7yYtIo-unsplash.jpg";
import sliderimage3 from "./Images/valeriia-bugaiova-_pPHgeHz1uk-unsplash.jpg";
import offerImg1 from "./Images/flight-offer-1.jpg";
import offerImg2 from "./Images/flight-offer-2.jpg";
import offerImg3 from "./Images/flight-offer-3.jpg";
import CarLoader from "../Hotel/CarLoader";

// TODO: add more hero photos to Images/ for a richer slider — reusing
// available images for now so the slider works out of the box
const HERO_SLIDES = [heroBg, sliderimage1, sliderimage2, sliderimage3];

const SLIDE_INTERVAL_MS = 5000;

// TODO: replace with real city-search API integration
const DEMO_CITY = {
  name: "PUNE",
  subtitle: "CITY IN, INDIA",
  country: "INDIA",
};

// Search results / navigation
const HOTEL_SEARCH_API = "https://api.hotel.namantechnolab.com/api/hotel/search";
const HOTEL_ENGINE_ID = "2";
const DEFAULT_NATIONALITY_CODE = "106";
const DEFAULT_COUNTRY_RES = "GB";
const DEFAULT_CURRENCY = "GBP";

// City / Country autosuggest API
const CITY_SEARCH_API =
  "https://api.hotel.namantechnolab.com/api/Static/CountryCitySearch?CityName=";
const CITY_SEARCH_MIN_CHARS = 3;
const CITY_SEARCH_DEBOUNCE_MS = 300;

// TODO: replace with real offers API integration
const DEMO_OFFERS = [
  { id: 1, image: offerImg1, alt: "Travel the world" },
  { id: 2, image: offerImg2, alt: "Explore the world" },
  { id: 3, image: offerImg3, alt: "Choose your destination" },
];

const WHY_BOOK_WITH_US = [
  {
    id: "quick",
    icon: Zap,
    title: "Quick Booking",
    text: "We provide quick and convenient flight bookings with amazing discounts.",
  },
  {
    id: "price",
    icon: Tag,
    title: "Lowest Price",
    text: "We ensure lowest pricing on hotel reservations and airline tickets.",
  },
  {
    id: "offers",
    icon: Gift,
    title: "Exciting Offers",
    text: "Enjoy exciting offers on flights, hotels and buses booking.",
  },
  {
    id: "support",
    icon: Headphones,
    title: "24/7 Support",
    text: "Get 24/7 support for booking related queries. We're here to help.",
  },
];

const STAR_OPTIONS = [1, 2, 3, 4, 5];

function formatDateParts(date) {
  const day = date.toLocaleDateString("en-GB", { day: "2-digit" });
  const month = date.toLocaleDateString("en-GB", { month: "short" });
  const year = date.toLocaleDateString("en-GB", { year: "numeric" });
  const weekday = date.toLocaleDateString("en-GB", { weekday: "long" });
  return { day, month, year, weekday };
}

function toISODate(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function nightsBetween(checkIn, checkOut) {
  const msPerDay = 24 * 60 * 60 * 1000;
  return Math.max(1, Math.round((startOfDay(checkOut) - startOfDay(checkIn)) / msPerDay));
}

function RoomsGuestsPanel({ rooms, setRooms, onDone }) {
  const updateAdult = (roomIndex, delta) => {
    setRooms((prev) =>
      prev.map((r, i) =>
        i === roomIndex
          ? { ...r, adults: Math.max(1, Math.min(9, r.adults + delta)) }
          : r
      )
    );
  };

  const updateChild = (roomIndex, delta) => {
    setRooms((prev) =>
      prev.map((r, i) =>
        i === roomIndex
          ? { ...r, children: Math.max(0, Math.min(9, r.children + delta)) }
          : r
      )
    );
  };

  const addRoom = () => {
    if (rooms.length >= 5) return;
    setRooms((prev) => [...prev, { adults: 1, children: 0 }]);
  };

  const removeRoom = () => {
    if (rooms.length <= 1) return;
    setRooms((prev) => prev.slice(0, -1));
  };

  return (
    <div className="hotel-search__pax-panel">
      {rooms.map((room, index) => (
        <div className="hotel-search__pax-room" key={index}>
          <h6 className="hotel-search__pax-room-title">Room {index + 1}</h6>
          <div className="hotel-search__pax-row">
            <div className="hotel-search__pax-counter">
              <span className="hotel-search__pax-counter-label">Adult(s)</span>
              <div className="hotel-search__pax-counter-controls">
                <button
                  type="button"
                  className="hotel-search__pax-btn"
                  onClick={() => updateAdult(index, -1)}
                  aria-label="Decrease adults"
                >
                  <Minus size={14} />
                </button>
                <span className="hotel-search__pax-value">{room.adults}</span>
                <button
                  type="button"
                  className="hotel-search__pax-btn"
                  onClick={() => updateAdult(index, 1)}
                  aria-label="Increase adults"
                >
                  <Plus size={14} />
                </button>
              </div>
            </div>
            <div className="hotel-search__pax-counter">
              <span className="hotel-search__pax-counter-label">Child(ren)</span>
              <div className="hotel-search__pax-counter-controls">
                <button
                  type="button"
                  className="hotel-search__pax-btn"
                  onClick={() => updateChild(index, -1)}
                  aria-label="Decrease children"
                >
                  <Minus size={14} />
                </button>
                <span className="hotel-search__pax-value">{room.children}</span>
                <button
                  type="button"
                  className="hotel-search__pax-btn"
                  onClick={() => updateChild(index, 1)}
                  aria-label="Increase children"
                >
                  <Plus size={14} />
                </button>
              </div>
            </div>
          </div>
        </div>
      ))}

      <div className="hotel-search__pax-actions">
        <button
          type="button"
          className="hotel-search__pax-add"
          onClick={addRoom}
          disabled={rooms.length >= 5}
        >
          Add Room
        </button>
        {rooms.length > 1 && (
          <button
            type="button"
            className="hotel-search__pax-remove"
            onClick={removeRoom}
          >
            Remove
          </button>
        )}
        <button type="button" className="hotel-search__pax-done" onClick={onDone}>
          Done
        </button>
      </div>
    </div>
  );
}

function CityAutosuggest({ city, setCity }) {
  const [query, setQuery] = useState(city.name || "");
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [error, setError] = useState(false);

  const wrapRef = useRef(null);
  const debounceRef = useRef(null);
  const abortRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    function handleClickOutside(event) {
      if (wrapRef.current && !wrapRef.current.contains(event.target)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Debounced fetch whenever the query changes
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    const trimmed = query.trim();
    if (trimmed.length < CITY_SEARCH_MIN_CHARS) {
      setSuggestions([]);
      setLoading(false);
      setError(false);
      return;
    }

    debounceRef.current = setTimeout(() => {
      fetchSuggestions(trimmed);
    }, CITY_SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  const fetchSuggestions = async (term) => {
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    setError(false);
    try {
      const res = await fetch(
        `${CITY_SEARCH_API}${encodeURIComponent(term)}`,
        { signal: controller.signal }
      );
      if (!res.ok) throw new Error("Network response was not ok");
      const data = await res.json();
      const list = Array.isArray(data) ? data : [];
      setSuggestions(list.slice(0, 20));
      setOpen(true);
      setActiveIndex(-1);
    } catch (err) {
      if (err.name !== "AbortError") {
        // TODO: surface a proper error state/toast if needed
        setError(true);
        setSuggestions([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = (item) => {
    const cityName = (item.CityName || "").toUpperCase();
    const countryName = (item.countryname || "").toUpperCase();
    const subtitle = countryName ? `CITY IN, ${countryName}` : "";
    setCity({ name: cityName, subtitle, country: countryName });
    setQuery(item.CityName || "");
    setOpen(false);
    setSuggestions([]);
    setActiveIndex(-1);
  };

  const handleKeyDown = (e) => {
    if (!open || suggestions.length === 0) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((prev) => (prev + 1) % suggestions.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((prev) => (prev - 1 + suggestions.length) % suggestions.length);
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (activeIndex >= 0 && activeIndex < suggestions.length) {
        handleSelect(suggestions[activeIndex]);
      }
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div className="hotel-search__field hotel-search__field--city" ref={wrapRef}>
      <span className="hotel-search__field-label">
        <MapPin size={14} /> City or Location
      </span>

      <input
        type="text"
        className="hotel-search__field-input"
        value={query}
        placeholder="Search city or country"
        onChange={(e) => setQuery(e.target.value)}
        onFocus={() => {
          if (suggestions.length > 0) setOpen(true);
        }}
        onKeyDown={handleKeyDown}
        autoComplete="off"
      />

      {!open && (
        <span className="hotel-search__field-sub">{city.subtitle}</span>
      )}

      {open && (
        <div className="hotel-search__city-suggestions">
          {loading && (
            <div className="hotel-search__city-suggestion hotel-search__city-suggestion--status">
              Searching...
            </div>
          )}

          {!loading && error && (
            <div className="hotel-search__city-suggestion hotel-search__city-suggestion--status">
              Couldn't load suggestions. Try again.
            </div>
          )}

          {!loading &&
            !error &&
            suggestions.length === 0 &&
            query.trim().length >= CITY_SEARCH_MIN_CHARS && (
              <div className="hotel-search__city-suggestion hotel-search__city-suggestion--status">
                No matching cities found.
              </div>
            )}

          {!loading &&
            !error &&
            suggestions.map((item, index) => (
              <button
                type="button"
                key={`${item.CityName}-${item.countryname}-${index}`}
                className={
                  "hotel-search__city-suggestion" +
                  (index === activeIndex
                    ? " hotel-search__city-suggestion--active"
                    : "")
                }
                onMouseEnter={() => setActiveIndex(index)}
                onClick={() => handleSelect(item)}
              >
                <span className="hotel-search__city-suggestion-city">
                  {item.CityName}
                </span>
                <span className="hotel-search__city-suggestion-country">
                  {item.countryname}
                </span>
              </button>
            ))}
        </div>
      )}
    </div>
  );
}

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
const WEEKDAY_LABELS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

function startOfDay(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

function isSameDay(a, b) {
  return (
    a &&
    b &&
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function buildMonthGrid(year, month) {
  const firstOfMonth = new Date(year, month, 1);
  const startWeekday = firstOfMonth.getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const cells = [];
  for (let i = 0; i < startWeekday; i++) cells.push(null);
  for (let day = 1; day <= daysInMonth; day++) {
    cells.push(new Date(year, month, day));
  }
  return cells;
}

function MonthTable({ year, month, minDate, checkIn, checkOut, hoverDate, onPick, onHover }) {
  const cells = buildMonthGrid(year, month);
  const min = startOfDay(minDate);

  return (
    <div className="hotel-search__cal-month">
      <div className="hotel-search__cal-month-title">
        {MONTH_NAMES[month]} {year}
      </div>
      <div className="hotel-search__cal-weekdays">
        {WEEKDAY_LABELS.map((w) => (
          <span key={w}>{w}</span>
        ))}
      </div>
      <div className="hotel-search__cal-grid">
        {cells.map((date, index) => {
          if (!date) {
            return <span key={`empty-${index}`} className="hotel-search__cal-cell hotel-search__cal-cell--empty" />;
          }

          const day = startOfDay(date);
          const disabled = day < min;
          const isCheckIn = isSameDay(day, checkIn);
          const isCheckOut = isSameDay(day, checkOut);

          const rangeEnd = checkOut || hoverDate;
          const inRange =
            checkIn &&
            rangeEnd &&
            day > startOfDay(checkIn) &&
            day < startOfDay(rangeEnd);

          const classNames = ["hotel-search__cal-cell"];
          if (disabled) classNames.push("hotel-search__cal-cell--disabled");
          if (isCheckIn || isCheckOut) classNames.push("hotel-search__cal-cell--selected");
          if (inRange) classNames.push("hotel-search__cal-cell--in-range");

          return (
            <button
              type="button"
              key={date.toISOString()}
              className={classNames.join(" ")}
              disabled={disabled}
              onClick={() => onPick(day)}
              onMouseEnter={() => onHover && onHover(day)}
            >
              {date.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function DateRangePicker({ checkIn, checkOut, setCheckIn, setCheckOut, minDate, onDone }) {
  const [viewDate, setViewDate] = useState(new Date(checkIn.getFullYear(), checkIn.getMonth(), 1));
  const [hoverDate, setHoverDate] = useState(null);

  const nextMonthDate = new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 1);

  const goPrevMonth = () => {
    const prev = new Date(viewDate.getFullYear(), viewDate.getMonth() - 1, 1);
    const minMonth = new Date(minDate.getFullYear(), minDate.getMonth(), 1);
    if (prev >= minMonth) setViewDate(prev);
  };

  const goNextMonth = () => {
    setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 1));
  };

  const handlePick = (day) => {
    // Starting fresh, or a full range already picked -> begin a new range
    if (!checkIn || (checkIn && checkOut)) {
      setCheckIn(day);
      setCheckOut(null);
      return;
    }
    // checkIn is set, checkOut isn't -> this click completes (or restarts) the range
    if (day <= startOfDay(checkIn)) {
      setCheckIn(day);
      setCheckOut(null);
    } else {
      setCheckOut(day);
    }
  };

  return (
    <div className="hotel-search__cal-panel">
      <div className="hotel-search__cal-header">
        <button
          type="button"
          className="hotel-search__cal-nav"
          onClick={goPrevMonth}
          aria-label="Previous month"
        >
          ‹
        </button>
        <span className="hotel-search__cal-hint">
          {checkOut ? "Select check-in / check-out" : "Select check-out date"}
        </span>
        <button
          type="button"
          className="hotel-search__cal-nav"
          onClick={goNextMonth}
          aria-label="Next month"
        >
          ›
        </button>
      </div>

      <div className="hotel-search__cal-months">
        <MonthTable
          year={viewDate.getFullYear()}
          month={viewDate.getMonth()}
          minDate={minDate}
          checkIn={checkIn}
          checkOut={checkOut}
          hoverDate={hoverDate}
          onPick={handlePick}
          onHover={setHoverDate}
        />
        <MonthTable
          year={nextMonthDate.getFullYear()}
          month={nextMonthDate.getMonth()}
          minDate={minDate}
          checkIn={checkIn}
          checkOut={checkOut}
          hoverDate={hoverDate}
          onPick={handlePick}
          onHover={setHoverDate}
        />
      </div>

      <div className="hotel-search__cal-actions">
        <button type="button" className="hotel-search__pax-done" onClick={onDone}>
          Done
        </button>
      </div>
    </div>
  );
}

export default function HotelSearchEngine({ onSearch }) {
  const today = new Date();
  const defaultCheckIn = new Date(today);
  defaultCheckIn.setDate(today.getDate() + 2);
  const defaultCheckOut = new Date(today);
  defaultCheckOut.setDate(today.getDate() + 3);

  const [selectedStars, setSelectedStars] = useState([]);
  const [checkIn, setCheckIn] = useState(defaultCheckIn);
  const [checkOut, setCheckOut] = useState(defaultCheckOut);
  const [rooms, setRooms] = useState([{ adults: 1, children: 0 }]);
  const [paxOpen, setPaxOpen] = useState(false);
  const paxRef = useRef(null);
  const [slideIndex, setSlideIndex] = useState(0);
  const [city, setCity] = useState(DEMO_CITY);
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const dateRef = useRef(null);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState(null);

  // Auto-rotate hero background images; search bar / content stay fixed on top
  useEffect(() => {
    const timer = setInterval(() => {
      setSlideIndex((prev) => (prev + 1) % HERO_SLIDES.length);
    }, SLIDE_INTERVAL_MS);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    function handleClickOutside(event) {
      if (paxRef.current && !paxRef.current.contains(event.target)) {
        setPaxOpen(false);
      }
      if (dateRef.current && !dateRef.current.contains(event.target)) {
        setDatePickerOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Auto-close the calendar once a full check-in/check-out range is chosen
  useEffect(() => {
    if (checkIn && checkOut) setDatePickerOpen(false);
  }, [checkIn, checkOut]);

  const handleSetCheckIn = (day) => {
    setCheckIn(day);
    if (checkOut && day >= startOfDay(checkOut)) {
      setCheckOut(null);
    }
  };

  const toggleStar = (star) => {
    setSelectedStars((prev) =>
      prev.includes(star) ? prev.filter((s) => s !== star) : [...prev, star]
    );
  };

  const totalAdults = rooms.reduce((sum, r) => sum + r.adults, 0);
  const checkInParts = formatDateParts(checkIn);
  const checkOutParts = checkOut
    ? formatDateParts(checkOut)
    : { day: "--", month: "", year: "", weekday: "Select date" };

  const buildPayload = () => {
    const roomCount = rooms.length;
    const totalChildren = rooms.reduce((sum, r) => sum + r.children, 0);
    const country = city.country || (city.subtitle || "").split(",").pop().trim();

    return {
      EngineId: HOTEL_ENGINE_ID,
      HotelDestination: `${city.name} , ${country}`,
      City: city.name,
      Country: country,
      CheckIn: toISODate(checkIn),
      CheckOut: checkOut ? toISODate(checkOut) : "",
      StartDate: toISODate(checkIn),
      EndDate: checkOut ? toISODate(checkOut) : "",
      countryres: DEFAULT_COUNTRY_RES,
      RoomCount: String(roomCount),
      Numofperson: `${totalAdults + totalChildren} `,
      Numofadults: totalAdults,
      NumofChild: totalChildren,
      Currency: DEFAULT_CURRENCY,
      Nationalitycode: DEFAULT_NATIONALITY_CODE,
      Night: checkOut ? String(nightsBetween(checkIn, checkOut)) : "1",
      room: rooms.map((r) => ({
        Adult: r.adults,
        child: Array.from({ length: r.children }, () => ({ Age: 0 })),
      })),
      HotelApiEngine: HOTEL_ENGINE_ID,
    };
  };

  const handleSearch = async () => {
    const hotelpayload = buildPayload();
    console.log("Hotel search payload:", hotelpayload);

    if (onSearch) {
      onSearch(hotelpayload);
      return;
    }

    setSearching(true);
    setSearchError(null);

    try {
      const res = await fetch(HOTEL_SEARCH_API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(hotelpayload),
      });

      if (!res.ok) {
        throw new Error(`Request failed with status ${res.status}`);
      }

      const data = await res.json();
      // This is the response the search should print to console.
      console.log("Hotel search response:", data);

      // Hand the payload + response off to the results page so it doesn't
      // need to hit the API again on load.
      try {
        sessionStorage.setItem("hotelSearchPayload", JSON.stringify(hotelpayload));
        sessionStorage.setItem("hotelSearchResponse", JSON.stringify(data));
      } catch (err) {
        console.error("Could not persist hotel search result", err);
      }

      window.location.href = "/hotel-results";
    } catch (err) {
      console.error("Hotel search request failed:", err);
      setSearchError(err.message || "Something went wrong while searching hotels.");
    } finally {
      setSearching(false);
    }
  };

  return (
    <section className="hotel-search">
      {searching && (
        <div className="car-loader-overlay">
          <CarLoader
            isLoading={searching}
            label={`Searching hotels in ${city.name}...`}
          />
        </div>
      )}

      <div className="hotel-search__hero">
        <div className="hotel-search__hero-slider" aria-hidden="true">
          {HERO_SLIDES.map((src, index) => (
            <div
              key={src}
              className={
                "hotel-search__hero-slide" +
                (index === slideIndex ? " hotel-search__hero-slide--active" : "")
              }
              style={{ backgroundImage: `url(${src})` }}
            />
          ))}
          <div className="hotel-search__hero-overlay" />
        </div>

        <div className="hotel-search__container hotel-search__hero-content">
          <h2 className="hotel-search__hero-title">
            Domestic &amp; International Hotels
          </h2>

          <div className="hotel-search__star-row">
            <span className="hotel-search__star-label">Preferred star rating</span>
            <div className="hotel-search__star-options">
              {STAR_OPTIONS.map((star) => (
                <label className="hotel-search__star-option" key={star}>
                  <input
                    type="checkbox"
                    checked={selectedStars.includes(star)}
                    onChange={() => toggleStar(star)}
                  />
                  <span>
                    {star} <Star size={12} />
                  </span>
                </label>
              ))}
            </div>
          </div>

          <div className="hotel-search__engine hotel-search__engine--sticky">
            <CityAutosuggest city={city} setCity={setCity} />

            <div className="hotel-search__date-group" ref={dateRef}>
              <button
                type="button"
                className="hotel-search__field hotel-search__field--date hotel-search__field--date-btn"
                onClick={() => setDatePickerOpen((open) => !open)}
              >
                <span className="hotel-search__field-label">
                  <Calendar size={14} /> Check-In
                </span>
                <span className="hotel-search__field-value">
                  {checkInParts.day} {checkInParts.month} {checkInParts.year}
                </span>
                <span className="hotel-search__field-sub">{checkInParts.weekday}</span>
              </button>

              <button
                type="button"
                className="hotel-search__field hotel-search__field--date hotel-search__field--date-btn"
                onClick={() => setDatePickerOpen((open) => !open)}
              >
                <span className="hotel-search__field-label">
                  <Calendar size={14} /> Check-Out
                </span>
                <span className="hotel-search__field-value">
                  {checkOutParts.day} {checkOutParts.month} {checkOutParts.year}
                </span>
                <span className="hotel-search__field-sub">{checkOutParts.weekday}</span>
              </button>

              {datePickerOpen && (
                <DateRangePicker
                  checkIn={checkIn}
                  checkOut={checkOut}
                  setCheckIn={handleSetCheckIn}
                  setCheckOut={setCheckOut}
                  minDate={today}
                  onDone={() => setDatePickerOpen(false)}
                />
              )}
            </div>

            <div className="hotel-search__field hotel-search__field--pax" ref={paxRef}>
              <span className="hotel-search__field-label">
                <Users size={14} /> Rooms &amp; Guests
              </span>
              <button
                type="button"
                className="hotel-search__field-value hotel-search__pax-trigger"
                onClick={() => setPaxOpen((open) => !open)}
              >
                {rooms.length} Room{rooms.length > 1 ? "s" : ""} &nbsp;
                {totalAdults} Adult{totalAdults > 1 ? "s" : ""}
              </button>

              {paxOpen && (
                <RoomsGuestsPanel
                  rooms={rooms}
                  setRooms={setRooms}
                  onDone={() => setPaxOpen(false)}
                />
              )}
            </div>

            <div className="hotel-search__field hotel-search__field--action">
              <button
                type="button"
                className="hotel-search__submit"
                onClick={handleSearch}
                disabled={searching}
              >
                <Search size={16} />
                {searching ? "SEARCHING..." : "SEARCH"}
              </button>
            </div>
          </div>

          {searchError && (
            <div className="hotel-search__error">{searchError}</div>
          )}
        </div>
      </div>

      <div className="hotel-search__container hotel-search__offers">
        <h3 className="hotel-search__section-title">
          <b>Hotel</b> Offers
        </h3>
        <div className="hotel-search__offers-grid">
          {DEMO_OFFERS.map((offer) => (
            <div className="hotel-search__offer-card" key={offer.id}>
              <img src={offer.image} alt={offer.alt} />
            </div>
          ))}
        </div>
      </div>

      <div className="hotel-search__why">
        <div className="hotel-search__container">
          <h3 className="hotel-search__section-title hotel-search__section-title--center">
            <b>Why Booking with Us</b>
          </h3>
          <div className="hotel-search__why-grid">
            {WHY_BOOK_WITH_US.map(({ id, icon: Icon, title, text }) => (
              <div className="hotel-search__why-card" key={id}>
                <div className="hotel-search__why-icon">
                  <Icon size={28} />
                </div>
                <h4>{title}</h4>
                <p>{text}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="hotel-search__app">
        <div className="hotel-search__container hotel-search__app-inner">
          <div className="hotel-search__app-copy">
            <h5>Get more out of our app with the mobile experience</h5>
            <p>
              Download the mobile app for one-touch access to your next travel
              adventure. You'll get access to hidden features and special offers.
            </p>
            <ul className="hotel-search__app-features">
              <li>Get exclusive offers &amp; prices</li>
              <li>Manage trip and ticket booking</li>
              <li>Activity suggestions based on destination</li>
              <li>All trip bookings in a single platform</li>
            </ul>
            <div className="hotel-search__app-links">
              <span className="hotel-search__app-link">
                <Smartphone size={16} /> Google Play
              </span>
              <span className="hotel-search__app-link">
                <Smartphone size={16} /> App Store
              </span>
            </div>
          </div>
          <div className="hotel-search__app-qr">
            <h2>QR Code</h2>
            <span>Scan to download</span>
          </div>
        </div>
      </div>
    </section>
  );
}